import { useState, useEffect } from 'react';
import {
	Box,
	Typography,
	SwipeableDrawer,
	Dialog,
	DialogContent,
	List,
	ListItemText,
	ListItemButton,
	Divider,
	Collapse,
	useTheme,
	useMediaQuery,
	IconButton,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Footer from './Footer';
import { useTranslation } from '../../hooks/useTranslation';
import { getEntityThemedCtaInlineStyle } from '../../utils/ctaContrast.utils';

const FAQ_ITEMS = [
	{
		id: 'what_is_finvu_aa',
		question: 'What is Finvu AA?',
		answer:
			'Finvu AA is an RBI-regulated Account Aggregator that securely transfers your financial information between financial institutions only after you give your consent.',
	},
	{
		id: 'how_finvu_uses_data',
		question: 'How does Finvu AA use my data?',
		answer:
			'Finvu AA does not access, analyze, or sell your financial data. It only facilitates the secure transfer of data between financial institutions after you approve a consent request.',
	},
	{
		id: 'is_data_safe',
		question: 'Is my financial data safe?',
		answer:
			"Yes. Your data is shared through secure, encrypted channels and only after you provide explicit consent. Finvu AA follows RBI's Account Aggregator framework for secure data sharing.",
	},
	{
		id: 'can_stop_sharing',
		question: 'Can I stop sharing my data?',
		answer:
			'Yes. You can view, manage, and revoke your active consents anytime in the Finvu AA app. After a consent is revoked, no future data requests can be made using it.',
	},
	{
		id: 'banking_credentials',
		question: 'Does Finvu AA have access to my banking password or OTP?',
		answer:
			'No. Finvu AA never asks for or stores your banking password, PIN, OTP, debit card details, or internet banking credentials.',
	},
	{
		id: 'why_share_data',
		question: 'Why am I being asked to share my financial data?',
		answer:
			'Financial institutions may request your data to provide services such as loans, investments, insurance, or personal finance management. The purpose of each request is shown before you decide whether to approve it.',
	},
];

function FaqDrawer({ customDetails }) {
	const [open, setOpen] = useState(false);
	const [expandedId, setExpandedId] = useState(null);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const isDark = customDetails?.darkTheme === true || theme.palette.mode === 'dark';
	const { t } = useTranslation();
	const contentBackground = isDark ? '#1C1C1C' : '#ffffff';

	useEffect(() => {
		const handler = () => setOpen(true);
		window.addEventListener('finvu:openFaq', handler);
		return () => window.removeEventListener('finvu:openFaq', handler);
	}, []);

	const handleToggle = (id, hasAnswer) => {
		if (!hasAnswer) {
			window.dispatchEvent(new CustomEvent('finvu:faqSupport'));
			return;
		}
		setExpandedId((prev) => (prev === id ? null : id));
	};

	const handleClose = () => {
		setOpen(false);
		setExpandedId(null);
	};

	const faqList = (
		<>
			<Box sx={{ px: '1.25rem', pt: '1.25rem', pb: '0.75rem' }}>
				<Typography
					sx={{
						fontWeight: 700,
						fontSize: '1.125rem',
						color: isDark ? '#ffffff' : '#1A1A1A',
						fontFamily: customDetails?.fontFamily,
					}}
				>
					Help
				</Typography>
				<Typography
					sx={{
						fontSize: '0.8125rem',
						color: isDark ? '#AAAAAA' : '#666666',
						mt: '0.25rem',
						fontFamily: customDetails?.fontFamily,
					}}
				>
					Here are some frequently asked questions (FAQs), that may help you
				</Typography>
			</Box>

			<List disablePadding>
				{FAQ_ITEMS.map((item, idx) => (
					<Box key={item.id}>
						<ListItemButton
							onClick={() => handleToggle(item.id, item.answer !== null)}
							sx={{
								px: '1.25rem',
								py: '1rem',
								'&:hover': {
									backgroundColor: isDark ? '#2A2A2A' : '#F9F9F9',
								},
							}}
						>
							<ListItemText
								primary={
									<Typography
										sx={{
											fontSize: '0.875rem',
											color: isDark ? '#ffffff' : '#1A1A1A',
											fontFamily: customDetails?.fontFamily,
										}}
									>
										{item.question}
									</Typography>
								}
							/>
							{item.answer !== null ? (
								expandedId === item.id ? (
									<ExpandMoreIcon
										sx={{
											color: isDark ? '#AAAAAA' : '#666666',
											fontSize: '1.25rem',
										}}
									/>
								) : (
									<ChevronRightIcon
										sx={{
											color: isDark ? '#AAAAAA' : '#666666',
											fontSize: '1.25rem',
										}}
									/>
								)
							) : null}
						</ListItemButton>

						{item.answer && (
							<Collapse in={expandedId === item.id} timeout="auto" unmountOnExit>
								<Box
									sx={{
										px: '1.25rem',
										pb: '1rem',
										backgroundColor: isDark ? '#252525' : '#F9F9F9',
									}}
								>
									<Typography
										sx={{
											fontSize: '0.8125rem',
											color: isDark ? '#CCCCCC' : '#555555',
											lineHeight: 1.6,
											fontFamily: customDetails?.fontFamily,
										}}
									>
										{item.answer}
									</Typography>
								</Box>
							</Collapse>
						)}

						{idx < FAQ_ITEMS.length - 1 && (
							<Divider sx={{ borderColor: isDark ? '#2D2D2D' : '#EEEEEE' }} />
						)}
					</Box>
				))}
			</List>
		</>
	);

	const understoodButton = (
		<Box
			sx={{
				flexShrink: 0,
				px: '1.25rem',
				pt: '1rem',
				pb: '1rem',
				borderTop: `1px solid ${isDark ? '#2D2D2D' : '#E0E0E0'}`,
				backgroundColor: contentBackground,
			}}
		>
			<Box
				component="button"
				type="button"
				onClick={handleClose}
				style={getEntityThemedCtaInlineStyle(customDetails, isDark, {
					width: '100%',
					padding: '10px 0',
					borderRadius: customDetails?.borderRadius || '8px',
					border: 'none',
					cursor: 'pointer',
					fontFamily: customDetails?.fontFamily,
					fontSize: '0.9375rem',
					fontWeight: 500,
					textTransform: 'capitalize',
					boxShadow: 'none',
				})}
			>
				{t('consentRenewal.buttons.understood')}
			</Box>
		</Box>
	);

	if (!isMobile) {
		return (
			<Dialog
				open={open}
				onClose={handleClose}
				fullWidth
				maxWidth="xs"
				disableEscapeKeyDown={false}
				sx={{
					'& .MuiDialog-paper': {
						borderRadius: '12px',
						overflow: 'hidden',
						backgroundColor: contentBackground,
					},
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'flex-end',
						p: '0.75rem 0.75rem 0',
					}}
				>
					<IconButton
						size="small"
						onClick={handleClose}
						aria-label={t('common.buttons.close')}
					>
						<CloseIcon
							fontSize="small"
							sx={{ color: isDark ? '#fff' : '#1A1A1A' }}
						/>
					</IconButton>
				</Box>
				<DialogContent
					sx={{
						p: 0,
						maxHeight: '70vh',
						overflowY: 'auto',
						backgroundColor: contentBackground,
					}}
				>
					{faqList}
					{understoodButton}
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<SwipeableDrawer
			anchor="bottom"
			open={open}
			onOpen={() => setOpen(true)}
			onClose={handleClose}
			disableSwipeToOpen
			disableEscapeKeyDown={false}
			PaperProps={{
				sx: {
					borderRadius: '1rem 1rem 0 0',
					maxHeight: '85vh',
					width: '100%',
					backgroundColor: contentBackground,
					display: 'flex',
					flexDirection: 'column',
				},
			}}
		>
			<Box
				sx={{
					width: 40,
					height: 4,
					borderRadius: 2,
					backgroundColor: isDark ? '#444' : '#CCCCCC',
					mx: 'auto',
					mt: '0.75rem',
					mb: '0.25rem',
					flexShrink: 0,
				}}
			/>
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: 'auto',
					backgroundColor: contentBackground,
				}}
			>
				{faqList}
			</Box>
			{understoodButton}
			<Footer customDetails={customDetails} embedded />
		</SwipeableDrawer>
	);
}

export default FaqDrawer;
